import { useEffect, useMemo, useState } from "react";
import { BookOpen, ChevronRight, Clock, HelpCircle, Search } from "lucide-react";
import { findGuide, guidesByCategory, GUIDES } from "./guides/index.js";
import type { Guide } from "./guides/types.js";

/**
 * Help Center — a single page with a left-column category tree + right-column
 * guide content. Each guide is a `.tsx` file in `guides/` exporting `meta` +
 * `Body()`. The registry imports them all and groups by category.
 *
 * Hash-routing: `#/help?guide=<id>` deep-links to a specific guide.
 * Operator can share a link by copying the URL from the browser.
 */
export function HelpPage() {
  const [selectedId, setSelectedId] = useState<string>(() => readGuideIdFromHash() ?? "getting-started");
  const [search, setSearch] = useState("");

  // Sync to hash so a deep link works on reload.
  useEffect(() => {
    if (window.location.hash.startsWith("#/help")) {
      window.location.hash = `/help?guide=${selectedId}`;
    }
  }, [selectedId]);

  // Listen for hash changes so back/forward navigation works.
  useEffect(() => {
    const onHash = () => {
      const id = readGuideIdFromHash();
      if (id && id !== selectedId) setSelectedId(id);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [selectedId]);

  const groups = useMemo(() => guidesByCategory(), []);
  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups
      .map((g) => ({ category: g.category, guides: g.guides.filter((x) => x.meta.title.toLowerCase().includes(q) || x.meta.description.toLowerCase().includes(q)) }))
      .filter((g) => g.guides.length > 0);
  }, [groups, search]);

  const guide = findGuide(selectedId) ?? GUIDES[0]!;
  const related = (guide.meta.relatedGuides ?? []).map(findGuide).filter((g): g is Guide => !!g);
  const recentlyUpdated = useMemo(() => {
    return [...GUIDES].sort((a, b) => (a.meta.lastUpdated < b.meta.lastUpdated ? 1 : -1)).slice(0, 3);
  }, []);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* Sidebar — category tree */}
      <aside className="space-y-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guides…"
            className="input pl-7 text-sm"
          />
        </div>

        <nav className="space-y-5">
          {filtered.length === 0 ? (
            <div className="text-xs text-muted">No guides match.</div>
          ) : filtered.map((g) => (
            <div key={g.category}>
              <div className="mb-1.5 px-2 text-2xs font-semibold uppercase tracking-wider text-muted/80">
                {g.category}
              </div>
              <ul className="space-y-0.5">
                {g.guides.map((x) => {
                  const active = x.meta.id === selectedId;
                  return (
                    <li key={x.meta.id}>
                      <button
                        onClick={() => setSelectedId(x.meta.id)}
                        className={
                          active
                            ? "flex w-full items-center justify-between gap-2 rounded-lg bg-brand-50 px-2.5 py-2 text-left text-sm font-medium text-brand-800 ring-1 ring-inset ring-brand/10"
                            : "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-muted-dark transition hover:bg-line/60 hover:text-ink"
                        }
                      >
                        <span className="truncate">{x.meta.title}</span>
                        <ChevronRight className={active ? "h-3.5 w-3.5 text-brand-700" : "h-3.5 w-3.5 text-muted"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="mt-4 rounded-lg bg-canvas p-3 ring-1 ring-line">
          <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-muted">
            <Clock className="h-3 w-3" />
            Recently updated
          </div>
          <ul className="space-y-1.5">
            {recentlyUpdated.map((g) => (
              <li key={g.meta.id}>
                <button
                  onClick={() => setSelectedId(g.meta.id)}
                  className="block w-full text-left text-xs text-muted-dark hover:text-ink hover:underline"
                >
                  {g.meta.title} <span className="text-muted">· {g.meta.lastUpdated}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Content */}
      <article className="card prose-sm max-w-none p-6">
        <header className="mb-5 border-b border-line pb-4">
          <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-brand">
            <BookOpen className="h-3.5 w-3.5" />
            {guide.meta.category}
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-ink">{guide.meta.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-2xs text-muted">
            <span>Last updated {guide.meta.lastUpdated}</span>
            {guide.meta.readTimeMin && <span>· {guide.meta.readTimeMin} min read</span>}
          </div>
        </header>

        <guide.Body />

        {related.length > 0 && (
          <div className="mt-8 border-t border-line pt-4">
            <div className="mb-2 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-muted">
              <HelpCircle className="h-3 w-3" />
              See also
            </div>
            <ul className="space-y-1">
              {related.map((r) => (
                <li key={r.meta.id}>
                  <button
                    onClick={() => setSelectedId(r.meta.id)}
                    className="text-sm font-medium text-brand-700 hover:underline"
                  >
                    {r.meta.title}
                  </button>
                  <span className="ml-2 text-2xs text-muted">{r.meta.description}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </div>
  );
}

function readGuideIdFromHash(): string | null {
  const hash = window.location.hash;
  const match = hash.match(/[?&]guide=([^&]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
}
